import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouterScrollBehavior,
} from "vue-router";
import navigation from "@/dictionaries/navigation.json";

const { pageTitles, pageMeta } = navigation.fr;

declare module "vue-router" {
  interface RouteMeta {
    // Title shown in the header banner, under the navigation
    pageTitle?: string;
    // False when the page renders its own <h1> (the banner title then is not a heading)
    pageTitleIsHeading?: boolean;
    // True on the home page only: scrolling ticker under the banner title
    showTicker?: boolean;
    // "Page texte" template (legal pages): smaller banner title aligned with the text column
    textPage?: boolean;
    // Browser tab title, before the site name (site name alone when not set)
    documentTitle?: string;
    // Content of the <meta name="description"> tag (no tag when not set)
    metaDescription?: string;
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    meta: { pageTitle: pageTitles.home, showTicker: true },
    component: () => import("../views/HomePage.vue"),
  },
  {
    path: "/a-propos",
    name: "about",
    meta: { pageTitle: pageTitles.about },
    component: () => import("../views/AboutPage.vue"),
  },
  {
    path: "/equipe",
    name: "team",
    meta: { pageTitle: pageTitles.team },
    component: () => import("../views/TeamPage.vue"),
  },
  {
    path: "/usages",
    name: "usages",
    meta: { pageTitle: pageTitles.usages },
    component: () => import("../views/UsagesPage.vue"),
  },
  // Former "Fonctionnalités" page, replaced by "Usages": keeps old links and bookmarks working
  {
    path: "/fonctionnalites",
    redirect: (to) => ({ path: "/usages", hash: to.hash }),
  },
  {
    path: "/offres-de-spectacles",
    name: "offers",
    meta: { pageTitle: pageTitles.offers },
    component: () => import("../views/OffersPage.vue"),
  },
  {
    path: "/offres-de-spectacles/:show",
    name: "offer",
    meta: { pageTitle: pageTitles.offers, pageTitleIsHeading: false },
    props: true,
    component: () => import("../views/ShowOfferPage.vue"),
  },
  {
    path: "/politique-de-confidentialite",
    name: "privacy",
    meta: {
      pageTitle: pageTitles.privacy,
      textPage: true,
      documentTitle: pageMeta.privacy.title,
      metaDescription: pageMeta.privacy.description,
    },
    component: () => import("../views/PrivacyPolicyPage.vue"),
  },
];

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition;
  } else if (to.hash) {
    // The router ignores the CSS scroll-margin-top of the target (room kept for a sticky element)
    const target = document.getElementById(decodeURIComponent(to.hash.slice(1)));
    const top = target ? parseFloat(getComputedStyle(target).scrollMarginTop) || 0 : 0;
    return { el: to.hash, top, behavior: "smooth" };
  } else {
    return { left: 0, top: 0 };
  }
};

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior,
});

const setMetaDescription = (description?: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!description) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = "description";
    document.head.appendChild(tag);
  }
  tag.content = description;
};

router.afterEach((to) => {
  document.title = to.meta.documentTitle
    ? `${to.meta.documentTitle} | ${pageMeta.siteName}`
    : pageMeta.siteName;
  setMetaDescription(to.meta.metaDescription);
});

export default router;
